<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const { uiSettings } = useUISettings();

const isSidebarOpen = computed(() => uiSettings.value.show_secondary_sidebar);

const tooltipText = computed(() => {
  return isSidebarOpen.value ? 'Fechar menu' : 'Abrir menu';
});

const handleClick = () => {
  emitter.emit(BUS_EVENTS.TOGGLE_SIDEMENU);
};
</script>

<template>
  <button
    v-tooltip.right="tooltipText"
    class="sidebar-toggle-button"
    :class="{
      'sidebar-closed': !isSidebarOpen
    }"
    @click="handleClick"
  >
    <div class="toggle-bar">
      <!-- Ícone de seta que muda conforme o estado -->
      <svg
        v-if="isSidebarOpen"
        class="toggle-icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M15 19l-7-7 7-7"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else
        class="toggle-icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M9 5l7 7-7 7"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.sidebar-toggle-button {
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  outline: none;

  .toggle-bar {
    width: 20px;
    height: 50px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--white) 40%,
      var(--white) 100%
    );
    border: 1px solid var(--s-200);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    transition: all 0.2s ease;

    &:hover {
      width: 24px;
      background: var(--white);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

      .toggle-icon {
        opacity: 0.8;
      }
    }

    &:active {
      background: var(--s-50);
    }
  }

  .toggle-icon {
    color: var(--s-500);
    opacity: 0.5;
    transition: opacity 0.2s ease;
  }

  &:focus-visible {
    .toggle-bar {
      outline: 2px solid var(--w-500);
      outline-offset: 1px;
    }
  }
}

// Dark mode
.dark {
  .sidebar-toggle-button {
    .toggle-bar {
      background: linear-gradient(
        90deg,
        transparent 0%,
        var(--s-800) 40%,
        var(--s-800) 100%
      );
      border-color: var(--s-700);

      &:hover {
        background: var(--s-800);
      }

      &:active {
        background: var(--s-900);
      }
    }

    .toggle-icon {
      color: var(--s-200);
    }
  }
}
</style>